// src/components/staff/StaffCalendarView.jsx
// Individual Staff Calendar — shows appointments + leave for the logged-in staff member
import React, { useState, useEffect } from 'react';
import {
  collection, onSnapshot, query, where, getDocs, doc, getDoc
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import '../../styles/booking-calendar.css';

function StaffCalendarView({ salonId, staffId }) {
  const [appointments, setAppointments] = useState([]);
  const [leaveData, setLeaveData]       = useState([]);
  const [services, setServices]         = useState([]);
  const [businessHoursData, setBusinessHoursData] = useState({
    regularHours: [], holidayClosures: [], customClosures: []
  });
  const [workingHoursData, setWorkingHoursData] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [currentDate, setCurrentDate]   = useState(new Date());

  // Modals
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedLeave, setSelectedLeave]   = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const SLOT_HEIGHT = 50;
  const timeSlots   = Array.from({ length: 30 }, (_, i) => 8 + i * 0.5);

  // ── helpers ─────────────────────────────────────────────────────────────────
  const getWeekDates = (date) => {
    const week        = [];
    const startOfWeek = new Date(date);
    const day         = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day + (day === 0 ? -6 : 1));
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      week.push(d);
    }
    return week;
  };

  const weekDates = getWeekDates(currentDate);

  const convertTimeToDecimal = (t) => {
    if (!t || t === '00:00') return 0;
    const [h, m] = t.split(':').map(Number);
    return isNaN(h) || isNaN(m) ? 0 : h + m / 60;
  };

  const formatTime = (slot) => {
    const h   = Math.floor(slot);
    const m   = (slot % 1) * 60;
    const per = h >= 12 ? 'PM' : 'AM';
    const dh  = h % 12 || 12;
    const dm  = m === 0 ? '00' : String(m).padStart(2, '0');
    return `${dh}:${dm} ${per}`;
  };

  const formatTime12Hour = (t24) => {
    if (!t24) return '';
    const [h, m] = t24.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return t24;
    const per = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${per}`;
  };

  const formatDateHeader = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const formatDateStr = (date) => date.toISOString().split('T')[0];

  const formatDayName = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'long' });

  // ── business-hours helpers ───────────────────────────────────────────────────
  const getBusinessHoursForDay = (date) => {
    if (!businessHoursData.regularHours.length) return null;
    const dayName    = formatDayName(date);
    const businessDay = businessHoursData.regularHours.find(b => b && b.day === dayName);
    if (!businessDay || businessDay.isClosed || !businessDay.open) return null;
    const openTime  = convertTimeToDecimal(businessDay.open);
    const closeTime = businessDay.close === '00:00' && businessDay.open !== '00:00'
      ? 24 : convertTimeToDecimal(businessDay.close);
    if (isNaN(openTime) || isNaN(closeTime)) return null;
    return { openTime, closeTime, businessDay };
  };

  const isClosedForBusinessHours = (date, slot) => {
    const bh = getBusinessHoursForDay(date);
    if (!bh) return { isClosed: true, reason: 'Closed – ' + formatDayName(date) };
    if (slot < bh.openTime || slot >= bh.closeTime)
      return { isClosed: true, reason: `Outside hours (${formatTime12Hour(bh.businessDay.open)}–${formatTime12Hour(bh.businessDay.close)})` };
    return { isClosed: false };
  };

  const isClosedForHolidays = (date) => {
    const ds = formatDateStr(date);
    const h  = businessHoursData.holidayClosures.find(c => c && c.date === ds);
    return h ? { isClosed: true, reason: `Holiday: ${h.name || 'Public Holiday'}` } : { isClosed: false };
  };

  const isClosedForCustom = (date, slot) => {
    const check = new Date(date); check.setHours(0, 0, 0, 0);
    for (const c of businessHoursData.customClosures) {
      if (!c || !c.startDate || !c.endDate) continue;
      const s = new Date(c.startDate); s.setHours(0, 0, 0, 0);
      const e = new Date(c.endDate);   e.setHours(23, 59, 59, 999);
      if (check >= s && check <= e) {
        if (c.isFullDay) return { isClosed: true, reason: `Closure: ${c.reason || 'Special closure'}` };
        if (c.startTime && c.endTime) {
          const cs = convertTimeToDecimal(c.startTime);
          const ce = convertTimeToDecimal(c.endTime);
          if (slot >= cs && slot < ce) return { isClosed: true, reason: `Closure: ${c.reason || 'Special closure'}` };
        }
      }
    }
    return { isClosed: false };
  };

  // ── staff helpers ────────────────────────────────────────────────────────────
  const isStaffOnLeave = (date, slot) => {
    const check = new Date(date); check.setHours(0, 0, 0, 0);
    for (const lv of leaveData) {
      if (!lv.startDate || !lv.endDate) continue;
      const s = new Date(lv.startDate); s.setHours(0, 0, 0, 0);
      const e = new Date(lv.endDate);   e.setHours(23, 59, 59, 999);
      if (check < s || check > e) continue;
      const typeLabel = lv.type === 'annual' ? 'Annual Leave' : lv.type === 'medical' ? 'Medical Leave' : 'Time Off';
      const bh = getBusinessHoursForDay(date);
      if (lv.timeType === 'full') {
        const from = bh ? bh.openTime  : 8;
        const to   = bh ? bh.closeTime : 18;
        if (slot >= from && slot < to) return { isOnLeave: true, reason: `${typeLabel}: ${lv.reason || 'Not specified'}`, leaveRecord: lv };
      } else if (lv.timeType === 'am' && slot >= 9 && slot < 13) {
        return { isOnLeave: true, reason: `${typeLabel} AM: ${lv.reason || 'Not specified'}`, leaveRecord: lv };
      } else if (lv.timeType === 'pm' && slot >= 13 && slot < 18) {
        return { isOnLeave: true, reason: `${typeLabel} PM: ${lv.reason || 'Not specified'}`, leaveRecord: lv };
      } else if (lv.timeType === 'custom' && lv.startTime && lv.endTime) {
        const ls = convertTimeToDecimal(lv.startTime);
        const le = convertTimeToDecimal(lv.endTime);
        if (slot >= ls && slot < le) return { isOnLeave: true, reason: `${typeLabel}: ${lv.reason || 'Not specified'}`, leaveRecord: lv };
      }
    }
    return { isOnLeave: false };
  };

  const isOutsideWorkingHours = (date, slot) => {
    if (!workingHoursData || workingHoursData.employmentType === 'fulltime') return false;
    const roster  = workingHoursData.roster || {};
    const year    = date.getFullYear();
    const month   = String(date.getMonth() + 1).padStart(2, '0');
    const day     = String(date.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${day}`;
    const slots   = roster[dateKey];
    if (!slots || !slots.length) return true;
    const h = Math.floor(slot);
    const m = Math.round((slot % 1) * 60);
    const ts = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    return !slots.includes(ts);
  };

  const getAppointmentsForSlot = (date, slot) => {
    const slotStart = new Date(date);
    slotStart.setHours(Math.floor(slot), (slot % 1) * 60, 0, 0);
    const slotEnd = new Date(slotStart);
    slotEnd.setMinutes(slotEnd.getMinutes() + 30);

    return appointments.filter(a => {
      if (!a.startDateTime || !a.endDateTime) return false;
      if (a.status !== 'pending' && a.status !== 'approved') return false;
      const s = new Date(a.startDateTime);
      const e = new Date(a.endDateTime);
      return s < slotEnd && e > slotStart;
    });
  };

  // ── slot class ───────────────────────────────────────────────────────────────
  const getSlotClass = (date, slot) => {
    const bhCheck = isClosedForBusinessHours(date, slot);
    const holCheck = isClosedForHolidays(date);
    const cusCheck = isClosedForCustom(date, slot);

    if (bhCheck.isClosed || holCheck.isClosed || cusCheck.isClosed)
      return { cls: 'time-slot closed', isClosed: true, reason: bhCheck.reason || holCheck.reason || cusCheck.reason };

    const leaveCheck = isStaffOnLeave(date, slot);
    if (leaveCheck.isOnLeave)
      return { cls: 'time-slot staff-on-leave', isClosed: false, leaveRecord: leaveCheck.leaveRecord, reason: leaveCheck.reason };

    if (isOutsideWorkingHours(date, slot))
      return { cls: 'time-slot outside-working-hours', isClosed: false };

    const slotStart = new Date(date);
    slotStart.setHours(Math.floor(slot), (slot % 1) * 60, 0, 0);
    const slotEnd = new Date(slotStart); slotEnd.setMinutes(slotEnd.getMinutes() + 30);

    const hasApproved = appointments.some(a =>
      a.status === 'approved' &&
      new Date(a.startDateTime) < slotEnd &&
      new Date(a.endDateTime) > slotStart
    );
    if (hasApproved) return { cls: 'time-slot approved', isClosed: false };

    return { cls: 'time-slot available', isClosed: false };
  };

  // ── data loading ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!salonId || !staffId) return;

    const init = async () => {
      try {
        setLoading(true);

        // Services
        try {
          const svcSnap = await getDocs(collection(db, 'services'));
          setServices(svcSnap.docs.filter(d => d.data().salonId === salonId).map(d => ({ id: d.id, ...d.data() })));
        } catch { setServices([]); }

        // Working hours for this staff
        try {
          const whQ  = query(collection(db, 'workingHours'), where('salonId', '==', salonId), where('staffId', '==', staffId));
          const whSn = await getDocs(whQ);
          if (!whSn.empty) {
            const wd = whSn.docs[0].data();
            setWorkingHoursData({ employmentType: wd.employmentType || 'fulltime', roster: wd.roster || {} });
          }
        } catch { /* no working hours doc */ }

        // Business hours (real-time)
        const salonRef = doc(db, 'salons', salonId);
        const unsubSalon = onSnapshot(salonRef, (snap) => {
          if (!snap.exists()) return;
          const data = snap.data();
          if (data.businessHours) {
            if (Array.isArray(data.businessHours)) {
              setBusinessHoursData({ regularHours: data.businessHours, holidayClosures: data.holidayClosures || [], customClosures: data.customClosures || [] });
            } else {
              setBusinessHoursData({
                regularHours:    data.businessHours.regularHours    || [],
                holidayClosures: data.businessHours.holidayClosures || [],
                customClosures:  data.businessHours.customClosures  || []
              });
            }
          }
        });

        setLoading(false);
        return unsubSalon;
      } catch (err) {
        console.error('StaffCalendarView init error:', err);
        setLoading(false);
      }
    };

    let unsubSalon;
    init().then(fn => { unsubSalon = fn; });
    return () => { if (unsubSalon) unsubSalon(); };
  }, [salonId, staffId]);

  // Appointments (real-time) — filter by staffId
  useEffect(() => {
    if (!salonId || !staffId) return;
    const q = query(collection(db, 'appointments'), where('salonId', '==', salonId), where('staffId', '==', staffId));
    const unsub = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => {
        const data = d.data();
        const startDateTime = data.dateTimeRequested?.toDate?.() ?? data.startDateTime?.toDate?.() ?? new Date(data.dateTimeRequested || data.startDateTime);
        let endDateTime;
        if (data.serviceRequested?.duration) {
          endDateTime = new Date(startDateTime);
          endDateTime.setMinutes(endDateTime.getMinutes() + data.serviceRequested.duration);
        } else if (data.endDateTime?.toDate) {
          endDateTime = data.endDateTime.toDate();
        } else if (data.endDateTime) {
          endDateTime = new Date(data.endDateTime);
        } else {
          endDateTime = new Date(startDateTime);
          endDateTime.setMinutes(endDateTime.getMinutes() + 60);
        }
        const serviceName = data.serviceRequested?.name || data.serviceName || 'Unknown Service';
        return { id: d.id, ...data, startDateTime, endDateTime, serviceName, duration: data.serviceRequested?.duration || 60 };
      });
      setAppointments(all);
    });
    return () => unsub();
  }, [salonId, staffId]);

  // Leave (real-time) — ALL statuses for this staff (staff sees pending too)
  useEffect(() => {
    if (!salonId || !staffId) return;
    const q = query(collection(db, 'leave'), where('salonId', '==', salonId), where('staffId', '==', staffId));
    const unsub = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => {
        const data = d.data();
        const startDate = data.startDate?.toDate?.() ?? new Date(data.startDate);
        const endDate   = data.endDate?.toDate?.()   ?? new Date(data.endDate);
        return { id: d.id, ...data, startDate, endDate };
      }).filter(l => l.startDate && l.endDate);
      setLeaveData(all);
    });
    return () => unsub();
  }, [salonId, staffId]);

  // ── navigation ───────────────────────────────────────────────────────────────
  const goToPrev  = () => { const d = new Date(currentDate); d.setDate(d.getDate() - 7); setCurrentDate(d); };
  const goToNext  = () => { const d = new Date(currentDate); d.setDate(d.getDate() + 7); setCurrentDate(d); };
  const goToToday = () => setCurrentDate(new Date());

  const formatLeaveTimeType = (lv) => {
    if (lv.timeType === 'full') return 'Full Day';
    if (lv.timeType === 'am')   return 'Morning (AM)';
    if (lv.timeType === 'pm')   return 'Afternoon (PM)';
    if (lv.timeType === 'custom' && lv.startTime && lv.endTime)
      return `${formatTime12Hour(lv.startTime)} – ${formatTime12Hour(lv.endTime)}`;
    return lv.timeType || 'Full Day';
  };

  const leaveStatusBadge = (status) => {
    const s = status || 'pending';
    const style = s === 'approved' ? { background: '#d4edda', color: '#155724' }
      : s === 'rejected' ? { background: '#f8d7da', color: '#721c24' }
      : { background: '#fff3cd', color: '#856404' };
    return (
      <span style={{ ...style, padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, textTransform: 'capitalize' }}>
        {s}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        Loading calendar…
      </div>
    );
  }

  return (
    <div className="booking-calendar-container" style={{ padding: 0, background: 'none', boxShadow: 'none' }}>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <button className="today-btn" onClick={goToToday} style={{ fontFamily: 'Familjen Grotesk, sans-serif', fontWeight: 600, padding: '7px 16px', borderRadius: '8px', border: '2px solid #FFBDB1', background: '#fff', color: '#E64C31', cursor: 'pointer' }}>Today</button>
        <button onClick={goToPrev}  style={{ fontFamily: 'Familjen Grotesk, sans-serif', fontWeight: 600, padding: '7px 16px', borderRadius: '8px', border: '2px solid #FFBDB1', background: '#fff', color: '#E64C31', cursor: 'pointer' }}>← Prev Week</button>
        <button onClick={goToNext}  style={{ fontFamily: 'Familjen Grotesk, sans-serif', fontWeight: 600, padding: '7px 16px', borderRadius: '8px', border: '2px solid #FFBDB1', background: '#fff', color: '#E64C31', cursor: 'pointer' }}>Next Week →</button>
        <span style={{ fontFamily: 'Wix Madefor Text, sans-serif', fontSize: '14px', color: '#666', marginLeft: '8px' }}>
          {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      </div>

      {/* Legend */}
      <div className="calendar-legend" style={{ marginBottom: '12px' }}>
        <div className="legend-item"><div className="legend-color available"></div><span>Available</span></div>
        <div className="legend-item"><div className="legend-color pending"></div><span>Pending Appt</span></div>
        <div className="legend-item"><div className="legend-color approved"></div><span>Confirmed Appt</span></div>
        <div className="legend-item"><div className="legend-color leave"></div><span>Leave</span></div>
        <div className="legend-item"><div className="legend-color unavailable"></div><span>Outside Hours</span></div>
        <div className="legend-item"><div className="legend-color outside-working-hours"></div><span>Outside Roster</span></div>
      </div>

      {/* Calendar grid */}
      <div className="calendar-grid-wrapper">
        {/* Header row */}
        <div className="calendar-header-row">
          <div className="time-header" style={{ height: SLOT_HEIGHT }}>Time</div>
          {weekDates.map(d => (
            <div key={d.toISOString()} className="day-header" style={{ height: SLOT_HEIGHT }}>
              {formatDateHeader(d)}
            </div>
          ))}
        </div>

        {/* Scrollable grid */}
        <div className="calendar-grid">
          {/* Time column */}
          <div className="time-column">
            {timeSlots.map(slot => (
              <div key={slot} className="time-slot-label" style={{ height: SLOT_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {formatTime(slot)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDates.map(date => (
            <div key={date.toISOString()} className="day-column">
              {timeSlots.map(slot => {
                const { cls, isClosed, reason, leaveRecord } = getSlotClass(date, slot);
                const slotAppts = getAppointmentsForSlot(date, slot);

                return (
                  <div key={`${date.toISOString()}-${slot}`} className={cls} style={{ height: SLOT_HEIGHT, cursor: 'default' }}>
                    {isClosed && reason && (
                      <div className="unavailability-overlay salon-level">
                        <div className="unavailability-text">{reason}</div>
                      </div>
                    )}

                    {!isClosed && leaveRecord && (
                      <div
                        className="unavailability-overlay staff-individual clickable"
                        style={{ cursor: 'pointer' }}
                        onClick={() => { setSelectedLeave(leaveRecord); setShowLeaveModal(true); }}
                      >
                        <div className="unavailability-text">{leaveRecord.reason || 'On Leave'}</div>
                      </div>
                    )}

                    {!isClosed && !leaveRecord && slotAppts.map(appt => (
                      <div
                        key={appt.id}
                        className={`booking-item ${appt.status || 'pending'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => { setSelectedAppointment(appt); setShowAppointmentModal(true); }}
                      >
                        <div className="booking-service">{appt.serviceName || 'Service'}</div>
                        <div className="booking-customer">{appt.customerName || appt.customerEmail || 'Customer'}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Appointment Modal (read-only) */}
      {showAppointmentModal && selectedAppointment && (
        <div className="modal-overlay" onClick={() => setShowAppointmentModal(false)}>
          <div className="booking-request-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Appointment Details</h3>
              <button className="close-button" onClick={() => setShowAppointmentModal(false)}>×</button>
            </div>
            <div className="modal-content">
              <div className="request-details">
                {[
                  ['Service',    selectedAppointment.serviceName],
                  ['Customer',   selectedAppointment.customerName || selectedAppointment.customerEmail],
                  ['Date',       selectedAppointment.startDateTime?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })],
                  ['Time',       selectedAppointment.startDateTime?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })],
                  ['End Time',   selectedAppointment.endDateTime?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })],
                  ['Duration',   `${selectedAppointment.duration} minutes`],
                  ['Status',     selectedAppointment.status?.toUpperCase()],
                ].map(([label, value]) => (
                  <div className="detail-row" key={label}>
                    <label>{label}:</label>
                    <span className={label === 'Status' ? `status ${selectedAppointment.status}` : ''}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Modal (read-only) */}
      {showLeaveModal && selectedLeave && (
        <div className="modal-overlay" onClick={() => setShowLeaveModal(false)}>
          <div className="booking-request-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Leave Details</h3>
              <button className="close-button" onClick={() => setShowLeaveModal(false)}>×</button>
            </div>
            <div className="modal-content">
              <div className="request-details">
                <div className="detail-row"><label>Leave Type:</label>
                  <span>{selectedLeave.type === 'annual' ? 'Annual Leave' : selectedLeave.type === 'medical' ? 'Medical Leave' : 'Time Off'}</span>
                </div>
                <div className="detail-row"><label>Time Type:</label><span>{formatLeaveTimeType(selectedLeave)}</span></div>
                <div className="detail-row"><label>Start Date:</label>
                  <span>{selectedLeave.startDate instanceof Date ? selectedLeave.startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}</span>
                </div>
                <div className="detail-row"><label>End Date:</label>
                  <span>{selectedLeave.endDate instanceof Date ? selectedLeave.endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}</span>
                </div>
                <div className="detail-row"><label>Reason:</label><span>{selectedLeave.reason || '-'}</span></div>
                {selectedLeave.notes && <div className="detail-row"><label>Notes:</label><span>{selectedLeave.notes}</span></div>}
                <div className="detail-row"><label>Status:</label>{leaveStatusBadge(selectedLeave.status)}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffCalendarView;
